/*! *****************************************************************************
File: Data-grid.template.ts
Author: Jared Craig - jared.craig@gm.com
Date: 10-10-2017    
Description: The Data Grid Template stores and maintains the state and
 configuration details used with the Data Grid
Other contributors: Neel Radia
***************************************************************************** */


import { FormGroup } from '@angular/forms';
import { State } from '@progress/kendo-data-query';
import { Subject ,  BehaviorSubject } from 'rxjs';
import { messages } from './data-grid.config';
import { FileRestrictions, SelectEvent, ClearEvent, RemoveEvent, FileInfo } from '@progress/kendo-angular-upload';
import { RowClassArgs } from '@progress/kendo-angular-grid';

export abstract class DataGridTemplate {
    editDataItem: any;
    data: any[];
    modal = false;
    httpErrorHeader: string;
    changed: Subject<any> = new BehaviorSubject<any>(null);
    gridDataChanged: Subject<any> = new BehaviorSubject<any>(null);
    httpResponse: Subject<any> = new BehaviorSubject<any>(null);
    isLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    detailTemplate: DataGridTemplate | any = undefined;
    detailTitleField: string = undefined;
    displayField: string = undefined;
    title: string = undefined;
    filterable: string = undefined;
    groupable = false;
    resizable = false;
    sortable = true;
    autoLock = false;
    isNotificationsEnabled = true;
    isSelectAll = false;
    editedRowIndex: string = undefined;
    isEditing: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    exportPDFClick: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    excelColumns: any[] = [];
    pdfColumns: any[] = [];
    fileName: string = undefined;
    timeStamp: Date = undefined;
    selectedRows: any[] = [];
    isServerSideFiltering: boolean;
    deleteValidation: boolean;
    saveUserSettings = true;
    configureSaveButton = true;
    selectableSettings = {
        enabled: false,
        checkboxOnly: true,
        mode: "multiple"
    };
    pageable: any = {
        buttonCount: 5,
        info: true,
        pageSizes: [10, 25, 50, 100]
    };

    uploadFiles: FileInfo[] = [];
    uploadRestrictions: FileRestrictions = {
        allowedExtensions: [],
    };
    pdfSettings: any = {
        landscape: false,
        scale: 0.8,
    }

    /**
     * The controls used by the grid
     * 'add' 'edit', 'delete', and 'excel' are default grid events with built in callbacks and are enabled by default
     * To desable a default control, set the control to false during initialization
     * 
     * ### Example
     * 
     * ```
     * ngOnInit() {
     *  this.controls.delete = false
     * }
     * ```
     * 
     * 
     * 'custom' and 'customToolbar' are lists of buttons with a custom callback.
     * 
     * ### Example
     * 
     * ```
     * myCustomControl = {
     *   id: "myCustomControl",
     *   iconClass: "fa fa-cogs",
     *   title: "Custom description on button hover",
     *   text: "Custom Button Label",
     *   callback: this.handleMyCustomControl,
     * }
     * 
     * this.controls.custom = [
     *   myCustomControl,
     * ]
     * 
     * or
     * 
     * this.controls.customToolbar = [
     *   myCustomControl,
     * ]
     * ```
     */
    controls = {
        add: true,
        excel: true,
        pdf: false,
        upload: false,
        edit: true,
        delete: true,
        configure: false,
        customButtons: [],
        customToolBarButtons: [],
        width: null,
        export: false,
        locked: false,
    }
    /**
     * The state of the grid
     */
    gridState: State = {
        sort: [],
        skip: 0,
        take: 25,
        group: [],
    };

    constructor() { }
    /**
     * The Data Grid (Read) event
     * @param force - if true, read should return latest data instead of cached
     */
    abstract read(opts: any): Promise<any>;
    /**
     * The Data Grid (Add New) event
     * @param parentEditDataItem - detail row parent editDataItem
     */
    abstract create(parentEditDataItem: any): void;
    /**
     * The Data Grid (Edit) event
     * @param dataItem - Edit dataItem for selected grid row
     */
    abstract edit(dataItem: any): void;
    /**
     * The Data Grid (Save) event
     * @param dataItem - value from editForm
     */
    abstract save(dataItem: any, isNew: boolean): Promise<any>;
    /**
     * 
     */
    submit = (dataItem: any, isNew: any): Promise<any> => null;
    /**
     * 
     */
    excel = (): Promise<any> => null;
    /**
     * The Data Grid (Delete) event
     * @param dataItem - Record to be deleted
     */
    abstract delete(dataItem: any): Promise<any>;
    /**
     * The customization properties for each column displayed in the grid
     * 
     * ### Example
     * 
     * ```
     * columns = [
     * {
     *   field: "ProductID",
     *   title: "Id",
     *   editable: false,
     *   width: 50
     * },
     * {
     *   field: "ProductName",
     *   title: "Name",
     * },
     * {
     *   field: "UnitPrice",
     *   title: "Unit Price",
     *   editor: "numeric",
     * }
     * ]
     * ```
     * 
     */
    abstract columns: GridColumnTemplate[];
    /**
     * The reactive form to track the state and validity while editing
     * 
     * ### Example
     * 
     * ```
     * editForm = this.formBuilder.group({
     *   ProductID: [],
     *   ProductName: ["", Validators.required],
     *   UnitPrice: [0]
     * })
     * ```
     * 
     */
    abstract editForm: FormGroup;


    /**
     * Implementaion for disabling controls on the grid - (disabled)
     * Override to return the state the controls.  Default is false (enabled).
     * @param id - unique id for the control e.g('edit', 'add', 'myCustomButton')
     * @param dataItem - dataItem for the selected row/record
     */
    readonly isDisabled = (id: string, dataItem: any): boolean => false;


    /**
    * Implementaion for enabling controls on the grid  (hidden)
    * Override to return the state the controls.  Default is true (visible).
    * @param id - unique id for the control e.g('edit', 'add', 'myCustomButton')
    * @param dataItem - dataItem for the selected row/record
   */
    readonly isEnabled = (id: string, dataItem: any): boolean => true;


    /**
    * Data Grid callback when the grid selection changes.
    */
    select = (data: any[]) => null;


    /**
     * Manually refresh the grid
     */
    refresh = () => this.changed.next(null);

    /**
     * Manually set the grid data
     */
    setGridData = () => this.gridDataChanged.next(null);

    /**
     * 
     */
    onCreate = (parentEditDataItem: any): void => {
        this.editDataItem = {};
        this.create(parentEditDataItem);
        this.editForm.reset(this.editDataItem);
    }


    /**
     * 
     */
    onEdit = (dataItem: any, rowIndex): void => {
        this.editDataItem = dataItem;
        this.edit(dataItem);
        this.editForm.reset(dataItem);
        this.editedRowIndex = rowIndex;
    }


    /**
     * Callback for save requests
     * Take response from the implementation of the save and build a response message.
     * Construct and send the response message to the Data Grid
     * @param dataItem - value of the form
     * @param isNew - create or update
     */
    onSave = ({ dataItem, isNew, isSubmit = false }) => {
        const response: Promise<any> = isSubmit ?
            this.submit(dataItem, isNew) :
            this.save(dataItem, isNew)

        const message = this.getSource(dataItem) +
            (isNew ? isSubmit ? messages.recordSubmitted : messages.recordCreated : isSubmit ? messages.recordSubmitted : messages.recordUpdated);

        this.httpResponse.next({
            response,
            message
        } as IHttpResponse);

        this.editedRowIndex = undefined;
        this.editDataItem = undefined;
    }


    /**
     * Check if displayField is set, then fetch the value for the displayField in dataItem
     * Return the fetched value if it is valid, otherwise return 'Record' (default)
     * @param dataItem - focused dataItem/record
     */
    getSource = (dataItem: any): string => {
        const source: string = this.displayField ? dataItem[this.displayField] : 'Record';
        return source ? source : 'Record';
    }

    getDeleteMessage = (dataItem: any): string => undefined;


    /**
     * custom service call to make when validation is needed before deleting an item.
     */
    deleteValidationService = (dataItem: any): Promise<string> => undefined;

    /**
     * set allowed file extensions for file uploading
     * @param extensions - array of file extensions in string
     */
    setAllowedUploadExtensions(extensions: string[]) {
        this.uploadRestrictions = {
            allowedExtensions: extensions,
        };
    }

    /**
     * 
     * @param context 
     * callback function to add certain html classs to a row
     * ex) retrun { highlight: true } will highlight the row
     */
    rowCallback = (context: RowClassArgs): any => '';

    /**
     * 
     * @param dataItem: data in a row
     * condition to decide wheather to show row detail 
     */
    showGridDetailCondition = (dataItem: any) => true;

    onSortChange = (dataItem: any) => { };
}


/**
 * 
 */
export class GridButton {
    id: string;
    iconClass: string;
    text: string;
    title: string;
    bare = false;
    togglable = false;
    enabled = true;
    primary = false;
    selected = false;
    refresh = true;
    callback: () => null;
}
/**
 * 
 */
abstract class GridColumnTemplate {
    type: string;
    field: string;
    title: string;
    hidden?: boolean;
    sortable?: boolean;
    filterable?: boolean;
    editable?: boolean;
    template?: any;
    detail?: any;
}
/**
 * 
 */
export class GridComboBox extends GridColumnTemplate {
    data: any[];
    textField: string;
    valueField: string;
    placeholder: string;
}
/**
 * 
 */
export class GridBoolean extends GridColumnTemplate {
    onLabel: string;
    offLabel: string;
}
/**
 * 
 */
interface IHttpResponse {
    response: Promise<any>;
    message: string;
}
